import React, { useRef, useState } from 'react';
import classNames from 'classnames';
import { FiEdit2, FiTrash2, FiMoreVertical } from 'react-icons/fi';

import { InitialsBadge } from '@/components/InitialsBadge';
import { RoleTag } from '@/views/EmployeeList/components/RoleTag.component';
import { Text, textVariantsEnum, fontWeightEnum } from '@/components/Text';
import { Button, buttonVariants } from '@/components/Button';
import { TextArea } from '@/components/TextArea';
import { ActionsMenu } from '@/components/ActionsMenu';
import { stripHtml } from '@/utils/html';
import { formatRelativeTime } from '../TaskDetail.utils';
import styles from './CommentThread.module.scss';

// Comments authored through this page's composer are always plain text.
// Older comments (authored via the previous CKEditor-based composer) are
// real HTML — detected here and stripped to plain text for display rather
// than rendered with dangerouslySetInnerHTML, so nothing in this render
// path ever executes untrusted markup.
const looksLikeHtml = (value) => /<[a-z][\s\S]*>/i.test(value || '');

export function CommentItem({
  comment,
  author,
  isReply = false,
  allowReply = true,
  canModify = false,
  onSaveEdit,
  onDelete,
  onSubmitReply,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(comment.comment);
  const [isReplying, setIsReplying] = useState(false);
  const [replyValue, setReplyValue] = useState('');
  const menuRef = useRef(null);

  const authorName = author?.userName || author?.userEmailid || 'Unknown user';
  const authorRole = author?.userType || author?.role;
  const bodyText = looksLikeHtml(comment.comment)
    ? stripHtml(comment.comment)
    : comment.comment;

  const menuItems = canModify
    ? [
        {
          label: 'Edit',
          icon: <FiEdit2 size={16} />,
          onClick: () => {
            setIsEditing(true);
            menuRef.current?.hide();
          },
        },
        {
          label: 'Delete',
          icon: <FiTrash2 size={16} />,
          danger: true,
          onClick: () => {
            onDelete(comment.id);
            menuRef.current?.hide();
          },
        },
      ]
    : [];

  return (
    <div
      className={classNames(styles.commentItem, {
        [styles.commentItemReply]: isReply,
      })}
    >
      <InitialsBadge label={authorName} twoLetter />
      <div className={styles.commentItemBody}>
        <div className={styles.commentItemHeader}>
          <Text
            variant={textVariantsEnum.Text_sm}
            fontWeight={fontWeightEnum.Semibold}
          >
            {authorName}
          </Text>
          {authorRole && <RoleTag role={authorRole} />}
          <Text
            variant={textVariantsEnum.Text_xs}
            color="gray-500"
            className={styles.commentTimestamp}
          >
            {formatRelativeTime(Number(comment.id))}
          </Text>
          {canModify && (
            <Button
              icon={<FiMoreVertical size={14} />}
              variant={buttonVariants.ghost}
              ariaLabel="Comment actions"
              className={styles.commentMenuTrigger}
              onClick={(e) => menuRef.current.toggle(e)}
            />
          )}
        </div>

        {isEditing ? (
          <div className={styles.commentEditBox}>
            <TextArea
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              rows={2}
            />
            <div className={styles.commentBoxActions}>
              <Button
                text="Save"
                variant={buttonVariants.primary}
                disabled={!editValue.trim()}
                onClick={() => {
                  onSaveEdit(comment.id, editValue.trim());
                  setIsEditing(false);
                }}
              />
              <Button
                text="Cancel"
                variant={buttonVariants.secondary}
                onClick={() => {
                  setIsEditing(false);
                  setEditValue(comment.comment);
                }}
              />
            </div>
          </div>
        ) : (
          <Text
            variant={textVariantsEnum.Text_sm}
            color="gray-700"
            className={styles.commentBodyText}
          >
            {bodyText}
          </Text>
        )}

        {!isEditing && allowReply && (
          <button
            type="button"
            className={styles.replyLink}
            onClick={() => setIsReplying((v) => !v)}
          >
            Reply
          </button>
        )}

        {isReplying && (
          <div className={styles.replyBox}>
            <TextArea
              value={replyValue}
              onChange={(e) => setReplyValue(e.target.value)}
              placeholder={`Reply to ${authorName}...`}
              rows={2}
            />
            <div className={styles.commentBoxActions}>
              <Button
                text="Reply"
                variant={buttonVariants.primary}
                disabled={!replyValue.trim()}
                onClick={() => {
                  onSubmitReply(comment.id, replyValue.trim());
                  setReplyValue('');
                  setIsReplying(false);
                }}
              />
              <Button
                text="Cancel"
                variant={buttonVariants.secondary}
                onClick={() => {
                  setIsReplying(false);
                  setReplyValue('');
                }}
              />
            </div>
          </div>
        )}
      </div>
      <ActionsMenu items={menuItems} ref={menuRef} />
    </div>
  );
}

CommentItem.displayName = 'CommentItem';
